/**
 * Calendar utility functions shared between schedule and reschedule components
 */

/**
 * Formats a date without timezone to YYYY-MM-DD format
 */
export function formatDateWithoutTimezone(date: Date | null): string {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(date);
}

/**
 * Formats a date with timezone to YYYY-MM-DD format
 */
export function formatDate(date: Date | null, timezone: string): string {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(date);
}

/**
 * Creates a validation function that checks if a date exists in the dateTimeMap
 */
export function createDateValidator(dateTimeMap: Map<string, string[]>) {
  return (d: Date | null): boolean => !!d && dateTimeMap.has(formatDateWithoutTimezone(d));
}

/**
 * Gets month key in YYYY-MM format for caching
 */
export function getMonthKey(date: Date): string {
  return date.toISOString().slice(0, 7); // "YYYY-MM"
}

/**
 * Calculates the end date for calendar availability (3 months ahead)
 */
export function calculateCalendarDateEnd(maxMonths: number): Date {
  const now = new Date();
  const targetMonth = new Date(now);
  targetMonth.setMonth(targetMonth.getMonth() + maxMonths);

  // go to next month & day 0 = last day of previous month
  const endOfMonthLocal = new Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 0, 23, 59, 59, 999);

  return endOfMonthLocal;
}

/**
 * Gets the start of a specific month
 */
export function getStartOfMonth(year: number, month: number): Date {
  return new Date(year, month, 1, 0, 0, 0, 0);
}

/**
 * Gets the end of a specific month
 */
export function getEndOfMonth(year: number, month: number): Date {
  return new Date(year, month + 1, 0, 23, 59, 59, 999);
}

/**
 * Helper to push value to a map list (creates list if doesn't exist)
 */
export function pushToMapList(map: Map<string, string[]>, key: string, value: string): void {
  const list = map.get(key) || [];
  if (list.length === 0) map.set(key, list);
  list.push(value);
}
